import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { publicApi } from '@/services/api';
import { useQuoteCart } from '@/context/QuoteCartContext';
import { toast } from 'sonner';
import { resolveImage, PRODUCT_IMAGE_BASE, SERVICE_IMAGE_BASE } from '@/lib/imageUrl';

// Animated counter component
const AnimatedCounter = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Animated text component - word-based for better responsiveness
const AnimatedTitle = ({ text, className = '' }) => {
  return (
    <span className={className}>
      {text.split(' ').map((word, idx) => (
        <span 
          key={idx} 
          className="inline-block animate-letter-appear opacity-0 mr-[0.3em]"
          style={{ animationDelay: `${idx * 0.15}s`, animationFillMode: 'forwards' }}
        >
          {word}
        </span>
      ))}
    </span>
  );
};

const HomeV2 = () => {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { addItem } = useQuoteCart();

  const heroSlides = [
    {
      image: '/images/main-slider/slider9/home-01.jpg',
      title: 'Acero',
      subtitle: 'En todas sus formas, medidas y espesores',
      highlight: 'Distribución'
    },
    {
      image: '/images/main-slider/slider9/home-02.jpg',
      title: 'Transformación',
      subtitle: 'Corte, doblez, rolado y más a tu medida',
      highlight: 'Maquinaria'
    },
    {
      image: '/images/main-slider/slider9/home-03.jpg',
      title: 'EN TUS GRANDES PROYECTOS',
      subtitle: 'Comprometidos con la mejor calidad y servicio',
      highlight: 'Ingenieria'
    }
  ];

  const stats = [
    { number: 17, suffix: '+', label: 'Años de Experiencia' },
    { number: 27, suffix: '', label: 'Productos' },
    { number: 13, suffix: '', label: 'Servicios' },
    { number: 4, suffix: '', label: 'Sucursales' },
  ];

  const processSteps = [
    { 
      step: '01', 
      title: 'Cuéntanos tu Proyecto', 
      desc: 'Te asesoramos para encontrar la mejor opción para tu proyecto.' 
    },
    { 
      step: '02', 
      title: 'Lo Fabricamos', 
      desc: 'Transformamos tu pedido con precisión y calidad.' 
    },
    { 
      step: '03', 
      title: 'Lo Entregamos', 
      desc: 'Llevamos tu pedido hasta el punto que nos indiques, asegurando entregas puntuales y seguimiento oportuno.' 
    },
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const loadData = async () => {
    try {
      const [productsRes, servicesRes] = await Promise.all([
        publicApi.getProducts(),
        publicApi.getServices()
      ]);
      setProducts(productsRes.data.slice(0, 8));
      setServices(servicesRes.data.slice(0, 6));
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleAddToCart = (product) => {
    addItem(product);
    toast.success(`${product.nombre} agregado al cotizador`);
  };

  return (
    <div className="bg-v2-darker">
      {/* Hero Section */}
      <section className="relative h-[68vh] min-h-[440px] md:h-[80vh] md:min-h-[560px] lg:h-screen lg:min-h-[640px] overflow-hidden" data-testid="hero-section-v2">
        {/* Background Slides */}
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1000 ${
              index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
            }`}
          >
            <div className="absolute inset-0 bg-black/50 z-10" />
            <img 
              src={slide.image} 
              alt={slide.title}
              className="w-full h-full object-cover"
            />
          </div>
        ))}

        {/* Hero Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-8 h-full flex items-center">
          <div className="max-w-3xl">
            {/* Highlight Tag */}
            <div className="inline-flex items-center gap-2 bg-v2-orange/20 border border-v2-orange/50 px-4 py-2 rounded-full mb-6">
              <span className="w-2 h-2 bg-v2-orange rounded-full animate-pulse" />
              <span className="text-v2-orange text-sm font-medium uppercase tracking-wider">
                {heroSlides[currentSlide].highlight}
              </span>
            </div>

            {/* Main Title */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 overflow-visible whitespace-normal break-words">
              <AnimatedTitle 
                key={currentSlide} 
                text={heroSlides[currentSlide].title} 
              />
            </h1>

            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-v2-text-muted mb-10 animate-fade-in" style={{ animationDelay: '0.5s' }}>
              {heroSlides[currentSlide].subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 animate-fade-in" style={{ animationDelay: '0.7s' }}>
              <Link 
                to="/productos" 
                className="group flex items-center gap-3 bg-v2-orange hover:bg-v2-orange-dark text-white font-bold uppercase tracking-wider px-8 py-4 transition-all duration-300"
                style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%)' }}
                data-testid="hero-products-btn-v2"
              >
                Ver Productos
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link 
                to="/contacto#contacto-form" 
                className="group flex items-center gap-3 border-2 border-white/30 hover:border-v2-orange text-white hover:text-v2-orange font-bold uppercase tracking-wider px-8 py-4 transition-all duration-300"
              >
                Contactar
              </Link>
            </div>
          </div>
        </div>

        {/* Slide Controls */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 hidden md:flex items-center gap-4">
          <button 
            onClick={() => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)}
            className="w-12 h-12 bg-white/10 hover:bg-v2-orange border border-white/20 rounded-full flex items-center justify-center text-white transition-all"
          >
            <ChevronLeft size={24} />
          </button>
          <div className="flex gap-2">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-1 rounded-full transition-all duration-500 ${
                  index === currentSlide ? 'w-12 bg-v2-orange' : 'w-4 bg-white/30'
                }`}
              />
            ))}
          </div>
          <button 
            onClick={() => setCurrentSlide((prev) => (prev + 1) % heroSlides.length)}
            className="w-12 h-12 bg-white/10 hover:bg-v2-orange border border-white/20 rounded-full flex items-center justify-center text-white transition-all"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 right-10 z-20 hidden lg:flex flex-col items-center gap-2">
          <span className="text-v2-text-muted text-sm uppercase tracking-wider rotate-90 origin-center translate-x-8">Scroll</span>
          <div className="w-0.5 h-16 bg-gradient-to-b from-v2-orange to-transparent" />
        </div>
      </section>

      {/* Stats Section */}
      {/*
      <section className="relative py-20 bg-v2-dark border-y border-v2-border">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center group">
                <div className="text-5xl md:text-6xl font-bold text-v2-orange mb-2 group-hover:scale-110 transition-transform">
                  <AnimatedCounter end={stat.number} suffix={stat.suffix} />
                </div>
                <p className="text-v2-text-muted uppercase tracking-wider text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* About Section */}
      <section className="py-24 relative overflow-hidden" data-testid="about-section-v2">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-v2-orange/5 to-transparent" />
        
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-4 border-l-4 border-v2-orange" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-4 border-r-4 border-v2-orange" />
              <img 
                src="/images/nosotros.jpg"
                alt="NAYACEROS"
                className="w-full h-[500px] object-cover"
              />
            </div>

            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-12 h-0.5 bg-v2-orange" />
                <span className="text-v2-orange uppercase tracking-wider text-sm font-medium">Nosotros</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
                EN TUS GRANDES PROYECTOS DESDE <span className="text-v2-orange">2007</span>
              </h2>
              
              <p className="text-v2-text-muted text-lg leading-relaxed mb-6">
                <strong className="text-white">NAYACEROS</strong> desde el 2007 somos una empresa dedicada a la distribución, transformación y fabricación de acero en todas sus formas, medidas y espesores.
              </p>
              
              <p className="text-v2-text-muted leading-relaxed mb-8">
                Distribuimos acero plano, estructural, galvanizado, tubulares, especiales,  para construcción, entre otros; además, ofrecemos servicios de corte, doblez, rolado, roscado, punzonado, corte láser, corte plasma y muchos más.
              </p>

               <p className="text-v2-text-muted leading-relaxed mb-8">
                Brindamos soluciones confiables con un enfoque técnico y de calidad para acompañarte en tus grandes proyectos.
              </p>

              <Link 
                to="/nosotros" 
                className="group inline-flex items-center gap-3 bg-v2-orange hover:bg-v2-orange-dark text-white font-bold uppercase tracking-wider px-8 py-4 transition-all duration-300"
                style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)' }}
              >
                Conocer Más
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-24" data-testid="products-section-v2">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="w-12 h-0.5 bg-v2-orange" />
              <span className="text-v2-orange uppercase tracking-wider text-sm font-medium">Catálogo</span>
              <span className="w-12 h-0.5 bg-v2-orange" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Nuestros <span className="text-v2-orange">Productos</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, idx) => (
              <div 
                key={product.id}
                className="group bg-v2-gray border border-v2-border hover:border-v2-orange overflow-hidden transition-all duration-300"
                data-testid={`product-card-v2-${product.id}`}
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={resolveImage(product.ruta_img, PRODUCT_IMAGE_BASE)}
                    alt={product.nombre}
                    className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => { e.target.src = 'https://placehold.co/400x300/1A1A1A/FF6B00?text=NAYACEROS'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-v2-darker via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <button
                    onClick={() => handleAddToCart(product)}
                    className="absolute bottom-4 right-4 w-12 h-12 bg-v2-orange hover:bg-v2-orange-dark text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300"
                    data-testid={`add-to-cart-v2-${product.id}`}
                  >
                    <Plus size={24} />
                  </button>
                  {product.categoria && (
                    <span className="absolute top-4 left-4 bg-v2-orange text-white text-xs font-bold px-3 py-1 uppercase tracking-wider">
                      {product.categoria}
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <Link to={`/v2/producto/${product.seo_url}`}>
                    <h3 className="text-lg font-bold text-white group-hover:text-v2-orange transition-colors uppercase">
                      {product.nombre}
                    </h3>
                  </Link>
                  <p className="text-v2-text-muted text-sm mt-2 line-clamp-2">{product.descripcion}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/productos" 
              className="group inline-flex items-center gap-3 bg-v2-orange hover:bg-v2-orange-dark text-white font-bold uppercase tracking-wider px-10 py-5 transition-all duration-300"
              style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%)' }}
              data-testid="products-cta-v2"
            >
              Ver Todos los Productos
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-v2-dark" data-testid="services-section-v2">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-12 h-0.5 bg-v2-orange" />
                <span className="text-v2-orange uppercase tracking-wider text-sm font-medium">Servicios</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white">
                Servicios de <span className="text-v2-orange">Transformación</span>
              </h2>
            </div>
            <Link 
              to="/servicios"
              className="mt-6 md:mt-0 text-v2-orange hover:text-v2-orange-light font-medium flex items-center gap-2 group"
            >
              Ver Todos
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <div 
                key={service.id}
                className="group bg-v2-gray border border-v2-border hover:border-v2-orange overflow-hidden transition-all duration-300 hover:-translate-y-2"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className="relative overflow-hidden h-44">
                  <img 
                    src={resolveImage(service.ruta_img, SERVICE_IMAGE_BASE)}
                    alt={service.nombre}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => { e.target.src = 'https://placehold.co/400x300/1A1A1A/FF6B00?text=Servicio'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-v2-darker via-transparent to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-v2-orange transition-colors uppercase">
                    {service.nombre}
                  </h3>
                  <p className="text-v2-text-muted text-sm leading-relaxed">
                    {service.descripcion}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-v2-dark relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-v2-border hidden lg:block" />
        
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="w-12 h-0.5 bg-v2-orange" />
              <span className="text-v2-orange uppercase tracking-wider text-sm font-medium">Proceso</span>
              <span className="w-12 h-0.5 bg-v2-orange" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Cómo <span className="text-v2-orange">Trabajamos</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {processSteps.map((item, idx) => (
              <div key={idx} className="relative text-center group">
                <div className="relative z-10 w-24 h-24 bg-v2-darker border-2 border-v2-orange rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-v2-orange transition-colors">
                  <span className="text-3xl font-bold text-v2-orange group-hover:text-white transition-colors">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-v2-text-muted">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/main-slider/slider9/home5.webp"
            alt="CTA Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-v2-darker/90" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-8 text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            ¿Listo para tu <span className="text-v2-orange">proyecto</span>?
          </h2>
          <p className="text-xl text-v2-text-muted max-w-2xl mx-auto mb-10">
            Contáctanos y encuentra la mejor solución en ACERO Y TRANSFORMACION para tu proyecto.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/contacto" 
              className="group flex items-center gap-3 bg-v2-orange hover:bg-v2-orange-dark text-white font-bold uppercase tracking-wider px-10 py-5 transition-all duration-300"
              style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%)' }}
            >
              Solicitar Cotización
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a 
              href="tel:3111292042" 
              className="flex items-center gap-3 border-2 border-white/30 hover:border-v2-orange text-white hover:text-v2-orange font-bold uppercase tracking-wider px-10 py-5 transition-all duration-300"
            >
              311 129 2042
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeV2;
